/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import {
  commonizeGreenLinesForExperiment,
  Experiment,
  generateBlueLinesForExperiment,
  generateGreenLinesForExperiment, generateViolationsForExperiment,
  selectCurrentStudyId,
  selectExperiment,
  selectGreenlineIdToProperties,
  selectGreenlineIdToRunState,
  selectStudy,
  smoothGreenLinesForExperiment,
  symmetrizeGreenLinesForExperiment
} from "../../../../../../redux/ducks/SensingSystemArchitectureDesign"
import {useAppDispatch, useAppSelector} from "../../../../../../redux/hooks"
import {useFetchToken} from "../../../../../../api/helper"
import {useEffect, useMemo, useState} from "react"
import {isGreenLineDataSetConfigured} from "../../helpers"
import {Divider, FormControl, FormHelperText, Popover} from "@mui/material"
import {ArgosMenu} from "../../../../../components/Menu/ArgosMenu"
import ArgosMenuItem from "../../../../../components/Form/Select/ArgosMenuItem"
import {ArgosExecuteIcon} from "../../../../../components/Icons/ArgosDatasetControlPanelIcons"
import SwitchRightIcon from '@mui/icons-material/SwitchRight';
import JoinFullIcon from '@mui/icons-material/JoinFull';
import ShapeLineIcon from '@mui/icons-material/ShapeLine';
import Tooltip from '@mui/material/Tooltip'
import {GreenLineDataSetStates} from "../../../types";
import {EAdasProgramSensorBomCheckboxTypes} from "../../../../AdasPrograms/ItemDetails/types";
import CheckObjectsBeforeBluelineGenerationDialog from "../BillOfMaterialsSection/Dialogs/CheckObjectsBeforeBluelineGenerationDialog/CheckObjectsBeforeBluelineGenerationDialog";
import ArgosTextField from "../../../../../components/Form/TextField/ArgosTextField"
import ArgosButton from "../../../../../components/Buttons/ArgosButton";
import {toast} from "react-hot-toast";

const validModalitiesforBlueLineGeneration = [EAdasProgramSensorBomCheckboxTypes.CAMERAS, EAdasProgramSensorBomCheckboxTypes.RADARS]

/**
 * Green Line Actions are the buttons that perform different types of actions
 * to produce green line data
 */
export const LineActions = () => {
  const dispatch = useAppDispatch()
  const fetchToken = useFetchToken()

  const currentStudyId = useAppSelector(selectCurrentStudyId)
  const study = useAppSelector(state => selectStudy(state, currentStudyId ?? ''))

  const experiment = useAppSelector(selectExperiment)
  const studyId = experiment?.studyId
  const selectedSensorsForGreenlineModify = experiment?.data?.selectedSensorsForGreenlineModify

  const adasProgramId = study?.adasProgramId

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [smoothnessFactor, setSmoothnessFactor] = useState<string>('')
  const smoothnessPopoverOpen = Boolean(anchorEl)
  const smoothnessFactorInvalid = isNaN(parseFloat(smoothnessFactor)) || parseFloat(smoothnessFactor) < 0 || parseFloat(smoothnessFactor) > 20
  const smoothnessPopoverId = smoothnessPopoverOpen ? 'smoothness-popover' : undefined

  const greenlineIdToRunState = useAppSelector(selectGreenlineIdToRunState)

  /**
   * Blue line run state
   */
  const sensorIdToBlueLineRunState = useAppSelector(state => state.sensingSystemArchitectureDesign.sensorIdToBlueLineRunState)
  // For each sensor id, check if it is in an in-progress state (e.g., PROCESSING, GENERATING)
  const isSensorIdToBlueLineInProgress = Object.values(sensorIdToBlueLineRunState).some(
    (item: any) => item.state === GreenLineDataSetStates.PROCESSING || item.state === GreenLineDataSetStates.GENERATING
  )

  /**
   * Violations run state
   */
  const sensorIdToViolationsRunState = useAppSelector(state => state.sensingSystemArchitectureDesign.sensorIdToViolationsRunState)
  // For each sensor id, check if it is in an in-progress state (e.g., PROCESSING, GENERATING)
  const isSensorIdToViolationsInProgress = Object.values(sensorIdToViolationsRunState).some(
    (item: any) => item.state === GreenLineDataSetStates.PROCESSING || item.state === GreenLineDataSetStates.GENERATING
  )

  const areAnySensorsNotValidModalityForBlueLineGeneration = useMemo(() => {
    if (!selectedSensorsForGreenlineModify) return false;
    const greenlineIds = Object.keys(selectedSensorsForGreenlineModify);
    if (!experiment?.data?.sensors) return false;
    const sensorConfig = experiment.data.sensors;

    // Map greenlineId to {modality, notSourced}
    const greenlineIdToInfo: Record<string, { modality?: string, notSourced: boolean }> = {};
    Object.values(sensorConfig).forEach((sensor: any) => {
      sensor.sensorLocations.forEach((location: any) => {
        if (greenlineIds.includes(location.greenlineDatasetId)) {
          const sensorId = location.sensorId ?? "";
          const notSourced = sensorId === "" || sensorId.endsWith("-not-sourced");
          greenlineIdToInfo[location.greenlineDatasetId] = {
            modality: sensor.sensorType,
            notSourced,
          };
        }
      });
    });

    // Returns true if any selected sensor is not a valid modality or is not sourced
    return greenlineIds.some(id => {
      const info = greenlineIdToInfo[id];
      return (
        !info ||
        info.notSourced ||
        !validModalitiesforBlueLineGeneration.includes(info.modality as EAdasProgramSensorBomCheckboxTypes)
      );
    });
  }, [selectedSensorsForGreenlineModify, experiment]);


  const [stateGeneratingGreenLines, setStateGeneratingGreenLines] = useState(false)
  const [stateCommonizingGreenLines, setStateCommonizingGreenLines] = useState(false)
  const [stateSmoothingGreenLines, setStateSmoothingGreenLines] = useState(false)
  const [stateSymmetrizingGreenLines, setStateSymmetrizingGreenLines] = useState(false)
  const anyInProgress = Object.values(greenlineIdToRunState).some((item: any) => item.inProgress === true);

  const greenlineIdToProperties = useAppSelector(selectGreenlineIdToProperties)
  const isDatasetInProgress = Object.values(greenlineIdToProperties).some(
    (item: any) => item.state === GreenLineDataSetStates.PROCESSING || item.state === GreenLineDataSetStates.GENERATING
  ) || stateGeneratingGreenLines;

  const showInProgress = anyInProgress || isDatasetInProgress

  // If there is change in greenlineIdToRunState, reset the states
  useEffect(() => {
    if (Object.keys(greenlineIdToRunState).length > 0) {
      setStateGeneratingGreenLines(false)
      setStateCommonizingGreenLines(false)
      setStateSmoothingGreenLines(false)
      setStateSymmetrizingGreenLines(false)
    }
  }, [greenlineIdToRunState]);


  /**
   * Green line actions disabled states:
   * Disable all buttons if any of the actions are loading;
   * Disable "Generate Green Lines" button if no sensors are selected;
   * Disable "Commonize" button if less than 2 sensors from "Modify Greenlines" column are selected;
   * Disable "Smooth" and "Symmetrize" if no sensors from "Modify Greenlines" column are selected
   */
  const isGreenlineActionsDisabledDueToLoading = stateGeneratingGreenLines || stateCommonizingGreenLines || stateSmoothingGreenLines || stateSymmetrizingGreenLines || showInProgress || isSensorIdToBlueLineInProgress || isSensorIdToViolationsInProgress

  // Check if there are any selected sensors that do not have objects or a redline selected
  const isGenerateGreenlinesDisabled = !selectedSensorsForGreenlineModify ||
    Object.keys(selectedSensorsForGreenlineModify).length === 0 ||
    Object.keys(selectedSensorsForGreenlineModify).some((_id) => {
      const sensorId = greenlineIdToProperties[_id]?.properties?.sensorId
      const locationKey = greenlineIdToProperties[_id]?.properties?.locationKey
      return !isGreenLineDataSetConfigured(sensorId, locationKey, experiment)
    })

  const isGenerateBlueLinesDisabled = areAnySensorsNotValidModalityForBlueLineGeneration || isGreenlineActionsDisabledDueToLoading || isGenerateGreenlinesDisabled;

  const isCommonizeDisabled = !selectedSensorsForGreenlineModify || Object.keys(selectedSensorsForGreenlineModify).length < 2
  const isSmoothDisabled = !selectedSensorsForGreenlineModify || Object.keys(selectedSensorsForGreenlineModify).length === 0
  const isSymmetrizeDisabled = !selectedSensorsForGreenlineModify || Object.keys(selectedSensorsForGreenlineModify).length === 0


  /** Open the smoothness factor popover */
  const handleOpenSmoothnessPopover = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  /** Close the smoothness factor popover */
  const handleCloseSmoothnessPopover = () => {
    setAnchorEl(null)
  }

  /** Set the smoothness factor value */
  const handleSmoothnessFactorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSmoothnessFactor(event.target.value)
  }

  /** Generate green lines for current workspace configuration */
  const handleGenerateGreenLines = () => {
    const generateGreenLinesForExperimentData = async (_adasProgramId: string, _experiment: Experiment) => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(generateGreenLinesForExperiment({idToken, adasProgramId: _adasProgramId, experiment: _experiment}))
    }
    if (adasProgramId && experiment) {
      setStateGeneratingGreenLines(true)
      generateGreenLinesForExperimentData(adasProgramId, experiment)
    }
  }

  /** Commonize green lines for current workspace configuration */
  const handleCommonizeGreenLines = () => {
    const commonizeGreenLinesForExperimentData = async (_adasProgramId: string, _experiment: Experiment) => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(commonizeGreenLinesForExperiment({idToken, adasProgramId: _adasProgramId, experiment: _experiment}))
    }
    if (adasProgramId && experiment) {
      setStateCommonizingGreenLines(true)
      commonizeGreenLinesForExperimentData(adasProgramId, experiment)
    }
  }

  /** Smooth green lines for current workspace configuration */
  const handleSmoothGreenLines = () => {
    const smoothGreenLinesForExperimentData = async (_adasProgramId: string, _experiment: Experiment, smoothnessFactor: number) => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(smoothGreenLinesForExperiment({idToken, adasProgramId: _adasProgramId, experiment: _experiment, smoothnessFactor}))
    }
    if (adasProgramId && experiment) {
      setStateSmoothingGreenLines(true)
      smoothGreenLinesForExperimentData(adasProgramId, experiment, parseFloat(smoothnessFactor))
      handleCloseSmoothnessPopover()
    }
  }

  /** Symmetrize green lines for current workspace configuration */
  const handleSymmetrizeGreenLines = () => {
    const symmetrizeGreenLinesForExperimentData = async (_adasProgramId: string, _experiment: Experiment) => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(symmetrizeGreenLinesForExperiment({idToken, adasProgramId: _adasProgramId, experiment: _experiment}))
    }
    if (adasProgramId && experiment) {
      setStateSymmetrizingGreenLines(true)
      symmetrizeGreenLinesForExperimentData(adasProgramId, experiment)
    }
  }

  /**
   * Generate blue lines for current selection
   * @param {Record<string, true>} sensorIdsToGenerate - The sensor IDs to generate blue lines for
   */
  const handleGenerateBlueLines = (sensorIdsToGenerate: Record<string, true>) => {
    const generateBlueLinesForExperimentData = async (_adasProgramId: string, _experiment: Experiment, sensorIdsToGenerate: Record<string, true>) => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(generateBlueLinesForExperiment({idToken, adasProgramId: _adasProgramId, experiment: _experiment, sensorIdsToGenerate}))
    }
    if (adasProgramId && experiment) {
      generateBlueLinesForExperimentData(adasProgramId, experiment, sensorIdsToGenerate)
    }
  }

  /** Warning dialog state */
  const [isRcsWarningDialogOpen, setIsRcsWarningDialogOpen] = useState(false)

  /**
   * Open the RCS warning dialog
   */
  const handleOpenRcsWarningDialog = () => {
    setIsRcsWarningDialogOpen(true)
  }

  /** Close the RCS warning dialog */
  const onCloseRcsWarningDialog = () => {
    setIsRcsWarningDialogOpen(false)
  }

  /**
   * Confirm the RCS warning dialog
   * @param {Record<string, true>} sensorIdsToGenerate - The sensor IDs to generate blue lines for
   */
  const onConfirmRcsWarningDialog = (sensorIdsToGenerate: Record<string, true>) => {
    setIsRcsWarningDialogOpen(false)
    handleGenerateBlueLines(sensorIdsToGenerate)
  }

  const bluelineDialogProps = useMemo(() => {
    return (
      {
        selectedSensorsForGreenlineModify,
        studyId,
        greenlineIdToProperties
      }
    )
  }, [selectedSensorsForGreenlineModify, studyId, greenlineIdToProperties])


  // Check if for every selected sensor, the blue line is completed
  const isBlueLineGenerationCompleted = useMemo(() => {
    if (!selectedSensorsForGreenlineModify || !Object.keys(selectedSensorsForGreenlineModify).length) {
      return false;
    }
    if (!sensorIdToBlueLineRunState) {
      return false;
    }
    return Object.keys(selectedSensorsForGreenlineModify).every((sensorId) => {
      return sensorIdToBlueLineRunState[sensorId]?.state === GreenLineDataSetStates.COMPLETE;
    });
  }, [selectedSensorsForGreenlineModify, sensorIdToBlueLineRunState]);

  // Check if for every selected sensor, the green line is completed
  const isGreenLineGenerationCompleted = useMemo(() => {
    if (!selectedSensorsForGreenlineModify || !Object.keys(selectedSensorsForGreenlineModify).length) {
      return false;
    }
    if (!greenlineIdToProperties) {
      return false;
    }
    return Object.keys(selectedSensorsForGreenlineModify).every((sensorId) => {
      return greenlineIdToProperties[sensorId]?.state === GreenLineDataSetStates.COMPLETE;
    });
  }, [selectedSensorsForGreenlineModify, greenlineIdToProperties]);


  const isGenerateViolationsEnabled = isBlueLineGenerationCompleted && isGreenLineGenerationCompleted
                                                && !isGreenlineActionsDisabledDueToLoading // TODO: If the states are completed then shouldnt be loading, but there could be slight delay in those state updates, whereas this var doesn't wait for backend to set.


  /**
   * Generate violations for current selection
   * @param _adasProgramId
   * @param _experiment
   * @param sensorIdsToGenerate
   */
  const handleGenerateViolations = async (_adasProgramId: string, _experiment: Experiment, sensorIdsToGenerate: Record<string, true>) => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(generateViolationsForExperiment({idToken, adasProgramId: _adasProgramId, experiment: _experiment, sensorIdsToGenerate}))
  }

  return (
    <ArgosFlexbox style={{width: '100%'}} colGap={10}>
      <ArgosMenu menuTitle="Green Line" menuId="green-line">
        <ArgosMenuItem
          onClick={handleGenerateGreenLines}
          disabled={isGreenlineActionsDisabledDueToLoading || isGenerateGreenlinesDisabled}
        >
          <ArgosExecuteIcon/>
          Generate
        </ArgosMenuItem>
        <Divider/>
        <ArgosMenuItem
          onClick={handleCommonizeGreenLines}
          disabled={isGreenlineActionsDisabledDueToLoading || isCommonizeDisabled}
        >
          <JoinFullIcon/>
          Commonize
        </ArgosMenuItem>
        <ArgosMenuItem
          onClick={handleOpenSmoothnessPopover}
          disabled={isGreenlineActionsDisabledDueToLoading || isSmoothDisabled}
          disableCloseOnClick={true}
        >
          <ShapeLineIcon/>
          Smooth
        </ArgosMenuItem>
        <ArgosMenuItem
          onClick={handleSymmetrizeGreenLines}
          disabled={isGreenlineActionsDisabledDueToLoading || isSymmetrizeDisabled}
        >
          <SwitchRightIcon/>
          Symmetrize
        </ArgosMenuItem>
      </ArgosMenu>
      <ArgosMenu menuTitle="Blue Line" menuId="blue-line">
        <Tooltip
          title={`Blue Line can only be generated for sourced sensors of type: ${validModalitiesforBlueLineGeneration.join(', ')}`}
          disableHoverListener={!areAnySensorsNotValidModalityForBlueLineGeneration}  /** Show tooltip only when Green Line is unchecked */
        >
          <span>
            <ArgosMenuItem
              onClick={() => handleOpenRcsWarningDialog()}
              disabled={isGenerateBlueLinesDisabled}
            >
              <ArgosExecuteIcon/>
              Generate Blue Lines
            </ArgosMenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={`Violations can only be generated if Blue Lines and Green Lines are in Complete state.`} /** Message saying why cannot generate violations */
          disableHoverListener={isGenerateViolationsEnabled}  /** Disable tooltip if it is valid to generate violations. */
        >
          <span>
            <ArgosMenuItem
              onClick={() => {
                if (!adasProgramId || !experiment || !selectedSensorsForGreenlineModify) {
                  toast.error('Must have non null adasProgramId, experiment, and selected sensors to generate violations')
                  return
                }
                handleGenerateViolations(adasProgramId, experiment, selectedSensorsForGreenlineModify)
              }}
              disabled={!isGenerateViolationsEnabled}
            >
              <ArgosExecuteIcon/>
              Generate Violations
            </ArgosMenuItem>
          </span>
        </Tooltip>
      </ArgosMenu>

      <Popover
        id={smoothnessPopoverId}
        open={smoothnessPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleCloseSmoothnessPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <ArgosFlexbox style={{padding: '10px'}}>
          <FormControl variant='standard'>
            <ArgosTextField
              id="smoothness-factor-textfield"
              inputProps={{"data-testid": "smoothness-factor-textfield"}}
              aria-describedby="smoothness-factor-helper-text"
              label="Smoothness Factor"
              type='number'
              required
              value={smoothnessFactor}
              onChange={handleSmoothnessFactorChange}
            />
            <FormHelperText id="smoothness-factor-helper-text">Allowed values are between 0 and 20</FormHelperText>
          </FormControl>
          <ArgosButton
            id='apply-smooth-green-lines-button'
            data-testid='apply-smooth-green-lines-button'
            variant="contained"
            size='large'
            onClick={handleSmoothGreenLines}
            disabled={smoothnessFactorInvalid}
          >
            Apply
          </ArgosButton>
        </ArgosFlexbox>
      </Popover>

      <CheckObjectsBeforeBluelineGenerationDialog
        data={bluelineDialogProps}
        open={isRcsWarningDialogOpen}
        onClose={onCloseRcsWarningDialog}
        onConfirm={onConfirmRcsWarningDialog}
      />

    </ArgosFlexbox>
  )
}
